import React, { useState } from 'react';
import { Button, Image, Input, Modal, Space, Table, Tag, Timeline, message } from 'antd';
import dayjs from 'dayjs';
import { useAuditApprovalMutations, useAuditDetail, usePendingAuditApprovals } from '../../hooks/useStudioOps';

function formatDate(value) {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '';
}

function skuThumb(row) {
    const src = row?.thumbUrl || row?.imageUrl;
    const preview = row?.imageUrl || row?.thumbUrl;
    return src ? <Image src={src} width={40} height={52} preview={preview ? { src: preview } : true} /> : '—';
}

function AuditDetail({ boxId }) {
    const { data, isFetching } = useAuditDetail(boxId);
    const detail = data?.data || {};
    const scanned = detail.auditScans || [];
    const pulled = detail.pulled || [];
    const missing = detail.missing || [];

    return (
        <div>
            <Table
                size="small"
                loading={isFetching}
                pagination={false}
                title={() => 'Scanned during audit'}
                rowKey={(r, i) => r._id || `${r.barcode}-${i}`}
                dataSource={scanned}
                columns={[
                    { title: 'Time', dataIndex: 'ts', render: formatDate, width: 160 },
                    { title: 'Barcode', dataIndex: 'barcode' },
                    { title: 'Result', dataIndex: 'result', render: r => <Tag>{r}</Tag> },
                    { title: 'Note', render: (_, r) => r.context?.note || r.context?.action || '' },
                    { title: 'Count', width: 80, render: (_, r) => r.context?.scanCount || '' },
                    { title: 'Operator', render: (_, r) => r.operator?.email || r.operator?.name || '' },
                ]}
            />
            <Table
                size="small"
                className="mt-2"
                pagination={false}
                title={() => 'Pulled during audit'}
                rowKey={r => String(r.skuId)}
                dataSource={pulled}
                columns={[
                    { title: 'Image', width: 70, render: (_, r) => skuThumb(r) },
                    { title: 'SKU', dataIndex: 'skuName' },
                    { title: 'EAN', dataIndex: 'EAN' },
                    { title: 'Order', dataIndex: 'orderName' },
                ]}
            />
            <Table
                size="small"
                className="mt-2"
                pagination={false}
                title={() => 'Missing SKUs'}
                rowKey={r => String(r.skuId)}
                dataSource={missing}
                expandable={{
                    expandedRowRender: row => (
                        <Timeline
                            items={(row.journey || []).map(ev => ({
                                children: (
                                    <span>
                                        {formatDate(ev.ts)} · {ev.type} · {ev.result} · {ev.operator?.email || ev.operator?.name || ''}
                                        {ev.barcode ? ` · ${ev.barcode}` : ''}
                                    </span>
                                ),
                            }))}
                        />
                    ),
                }}
                columns={[
                    { title: 'Image', width: 70, render: (_, r) => skuThumb(r) },
                    { title: 'SKU', dataIndex: 'skuName' },
                    { title: 'EAN', dataIndex: 'EAN' },
                    { title: 'Order', dataIndex: 'orderName' },
                    { title: 'Packed by', dataIndex: 'packedBy' },
                    { title: 'Packed at', dataIndex: 'packedAt', render: formatDate },
                ]}
            />
        </div>
    );
}

export default function AuditApprovals() {
    const { data, isFetching } = usePendingAuditApprovals();
    const mutations = useAuditApprovalMutations();
    const [expanded, setExpanded] = useState([]);

    const approve = async boxId => {
        try {
            await mutations.approve.mutateAsync({ boxId, note: '' });
            message.success('Audit approved; missing SKUs returned to inventory');
        } catch (err) {
            message.error(err?.response?.data?.message || 'Could not approve');
        }
    };

    const reject = boxId => {
        let reason = '';
        Modal.confirm({
            title: `Reject audit for ${boxId}`,
            content: <Input.TextArea rows={2} placeholder="Reason" onChange={e => (reason = e.target.value)} />,
            onOk: () => {
                if (!reason.trim()) {
                    message.error('Reason required');
                    return Promise.reject();
                }
                return mutations.reject.mutateAsync({ boxId, note: reason.trim() }).then(() => {
                    message.success('Audit rejected; box reopened');
                });
            },
        });
    };

    return (
        <div style={{ padding: 16 }}>
            <Table
                loading={isFetching}
                rowKey="boxId"
                dataSource={data?.data?.data || []}
                expandable={{
                    expandedRowKeys: expanded,
                    onExpand: (open, row) => setExpanded(open ? [row.boxId] : []),
                    expandedRowRender: row => <AuditDetail boxId={row.boxId} />,
                }}
                columns={[
                    { title: 'Box', dataIndex: 'boxId' },
                    { title: 'Client', dataIndex: 'companyCode' },
                    { title: 'Destination', dataIndex: 'destination' },
                    { title: 'Auditor', render: (_, r) => r.auditor?.email || r.auditor?.name || '' },
                    { title: 'Submitted', dataIndex: 'submittedAt', render: formatDate },
                    { title: 'Expected', dataIndex: 'itemsExpected', width: 100 },
                    { title: 'Scanned', dataIndex: 'itemsMatched', width: 90 },
                    { title: 'Missing', dataIndex: 'missing', width: 90 },
                    {
                        title: 'Action',
                        width: 180,
                        render: (_, row) => (
                            <Space>
                                <Button size="small" type="primary" loading={mutations.approve.isLoading} onClick={() => approve(row.boxId)}>
                                    Approve
                                </Button>
                                <Button size="small" danger onClick={() => reject(row.boxId)}>
                                    Reject
                                </Button>
                            </Space>
                        ),
                    },
                ]}
            />
        </div>
    );
}
